/**
 * Password strength scoring + secure generation.
 *
 * The policy here is the single source of truth for the *client* and must stay
 * in lockstep with the server-side `password_policy_check()` in
 * public/api/password-policy.php: minimum 8 characters with a mix of lower,
 * upper, and digit. Symbols are not required but strengthen the score.
 */

export const PASSWORD_MIN_LENGTH = 8;

export interface PasswordChecks {
  length: boolean;
  lower: boolean;
  upper: boolean;
  digit: boolean;
  symbol: boolean;
}

export interface PasswordEvaluation {
  /** 0 = empty, 1 = weak … 4 = strong. Drives the gauge. */
  level: 0 | 1 | 2 | 3 | 4;
  /**
   * i18n key for the strength label, or '' at level 0 (nothing typed yet).
   * A key rather than text: this module is pure scoring and has no business
   * knowing a language — PasswordField translates it at render.
   */
  labelKey: string;
  /** Bar/label colour for this level. */
  color: string;
  /** Meets the minimum acceptance bar (length + lower + upper + digit). */
  meetsPolicy: boolean;
  checks: PasswordChecks;
}

const LEVELS: { labelKey: string; color: string }[] = [
  { labelKey: '', color: 'transparent' },
  { labelKey: 'auth:password.strength.weak', color: '#dc3545' },     // red
  { labelKey: 'auth:password.strength.fair', color: '#f0ad4e' },     // amber
  { labelKey: 'auth:password.strength.good', color: '#e0c000' },     // yellow-green
  { labelKey: 'auth:password.strength.strong', color: '#28a745' },   // green
];

export function evaluatePassword(pw: string): PasswordEvaluation {
  const checks: PasswordChecks = {
    length: pw.length >= PASSWORD_MIN_LENGTH,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    digit: /[0-9]/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
  const meetsPolicy = checks.length && checks.lower && checks.upper && checks.digit;

  if (!pw) {
    return { level: 0, labelKey: LEVELS[0].labelKey, color: LEVELS[0].color, meetsPolicy, checks };
  }

  const classes = [checks.lower, checks.upper, checks.digit, checks.symbol].filter(Boolean).length;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  if (classes >= 2) score++;
  if (classes >= 3) score++;
  if (classes >= 4) score++;

  // Map the raw 0..6 score onto 1..4 gauge levels.
  let level: 1 | 2 | 3 | 4 = score <= 1 ? 1 : score <= 3 ? 2 : score <= 4 ? 3 : 4;
  // Never show "green" while a required class is still missing.
  if (!meetsPolicy && level > 2) level = 2;

  return { level, labelKey: LEVELS[level].labelKey, color: LEVELS[level].color, meetsPolicy, checks };
}

// Unambiguous character sets (no l/1/I, O/0) for readable generated passwords.
const GEN_LOWER = 'abcdefghijkmnpqrstuvwxyz';
const GEN_UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const GEN_DIGIT = '23456789';
const GEN_SYMBOL = '!@#$%^&*?-_=+';

/** Cryptographically-random integer in [0, max). */
function randInt(max: number): number {
  const buf = new Uint32Array(1);
  // Reject values in the unfair tail so the modulo is unbiased.
  const limit = Math.floor(0xffffffff / max) * max;
  let x = 0;
  do {
    window.crypto.getRandomValues(buf);
    x = buf[0];
  } while (x >= limit);
  return x % max;
}

function pick(chars: string): string {
  return chars.charAt(randInt(chars.length));
}

/**
 * Generate a strong password guaranteed to satisfy the policy (one of each
 * class), Fisher–Yates shuffled so the guaranteed characters aren't positional.
 */
export function generatePassword(length = 16): string {
  const all = GEN_LOWER + GEN_UPPER + GEN_DIGIT + GEN_SYMBOL;
  const out: string[] = [pick(GEN_LOWER), pick(GEN_UPPER), pick(GEN_DIGIT), pick(GEN_SYMBOL)];
  for (let i = out.length; i < length; i++) out.push(pick(all));
  for (let i = out.length - 1; i > 0; i--) {
    const j = randInt(i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.join('');
}
